import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Divider,
  useTheme
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ScatterChart,
  Scatter,
  ZAxis,
  Label,
  ReferenceLine
} from 'recharts';
import { format, subDays } from 'date-fns';
import useSensorData from '../hooks/useSensorData';
import SensorTimeSeries from './SensorTimeSeries';
import CustomTooltip from './CustomTooltip';
import { getSensorsForTunnel } from '../api/sensorApi';

const timeRanges = [
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 180, label: '6 Months' },
  { value: 365, label: '1 Year' }
];

const ComparisonView = () => {
  const theme = useTheme();
  const { tunnelId } = useParams();
  const navigate = useNavigate();
  const { loading, data, error, selectedTunnel, changeTunnel } = useSensorData(tunnelId);
  const [timeRange, setTimeRange] = useState(30);
  const [filteredData, setFilteredData] = useState([]);
  const [correlationData, setCorrelationData] = useState([]);
  const [diffData, setDiffData] = useState([]);
  const [availableSensors, setAvailableSensors] = useState([]);
  const [selectedSensor1, setSelectedSensor1] = useState('m1');
  const [selectedSensor2, setSelectedSensor2] = useState('m2');
  const [readingType, setReadingType] = useState('m1'); // 'm1' or 'm2'
  const [stats, setStats] = useState({
    correlation: 0,
    averageDiff: 0,
    maxDiff: 0,
    sensor1Avg: 0,
    sensor2Avg: 0
  });
  
  // Load available sensors when tunnel changes
  useEffect(() => {
    const loadSensors = async () => {
      if (selectedTunnel) {
        try {
          const sensors = await getSensorsForTunnel(selectedTunnel);
          console.log('Available sensors for tunnel:', sensors);
          setAvailableSensors(sensors);
          
          // Set default sensors if available
          if (sensors.length >= 2 && selectedSensor1 === 'm1' && selectedSensor2 === 'm2') {
            // Only set default values if we haven't already selected specific sensors
            setSelectedSensor1(sensors[0].id);
            setSelectedSensor2(sensors[1].id);
          }
        } catch (error) {
          console.error('Error loading sensors:', error);
        }
      }
    };
    
    loadSensors();
  }, [selectedTunnel, selectedSensor1, selectedSensor2]);

  // This effect ensures we rerender the sensor time series when sensor selection changes
  useEffect(() => {
    console.log('Sensors or reading type changed: ', selectedSensor1, selectedSensor2, readingType);
  }, [selectedSensor1, selectedSensor2, readingType]);
  
  // Filter data based on selected time range
  useEffect(() => {
    if (data?.sensorData) {
      const startDate = subDays(new Date(), timeRange);
      
      // Filter sensor data
      // Filter sensor data and convert timestamps to numeric format
      let filtered = data.sensorData
        .filter(item => {
          try {
            const itemDate = new Date(item.timestamp);
            return itemDate >= startDate;
          } catch (e) {
            return false;
          }
        })
        .map(item => {
          // Create a new object with the same properties
          const newItem = {...item};
          
          // Convert timestamp to numeric timestamp for charts
          try {
            newItem.timestamp = new Date(item.timestamp).getTime();
          } catch (e) {
            console.error("Error converting timestamp:", item.timestamp);
          }
          
          return newItem;
        });
      
      console.log(`Filtered to ${filtered.length} data points, sample:`, 
        filtered.length > 0 ? filtered[0] : 'No data');
      
      setFilteredData(filtered);
      
      // Use either the special format keys or regular keys for selected sensors
      const getSensorValue = (item, sensorId, readingTypeOverride = null) => {
        const readingToUse = readingTypeOverride || readingType;
        
        // Handle "m1" and "m2" overall averages
        if (sensorId === 'm1') return item.m1;
        if (sensorId === 'm2') return item.m2;
        
        // For specific sensors, first check if we have a formatted key with reading type
        const specialKey = `${sensorId}_${readingToUse}`;
        if (item[specialKey] !== undefined && item[specialKey] !== null) {
          return item[specialKey];
        }
        
        // Otherwise fall back to the direct sensor key
        return item[sensorId];
      };
      
      // Debug sensor data availability 
      if (selectedSensor1 !== 'm1' || selectedSensor2 !== 'm2') {
        // Sample a data point to debug
        const sampleItem = filtered[Math.floor(filtered.length / 2)];
        if (sampleItem) {
          console.log('Sample data point for sensor check:', sampleItem);
          
          // Check for sensor 1
          if (selectedSensor1 !== 'm1') {
            const specialKey1 = `${selectedSensor1}_${readingType}`;
            console.log(`Sensor 1 (${selectedSensor1}) data availability:`,
              `Special key (${specialKey1}):`, sampleItem[specialKey1] !== undefined,
              `Direct key:`, sampleItem[selectedSensor1] !== undefined);
          }
          
          // Check for sensor 2
          if (selectedSensor2 !== 'm2') {
            const specialKey2 = `${selectedSensor2}_${readingType}`;
            console.log(`Sensor 2 (${selectedSensor2}) data availability:`, 
              `Special key (${specialKey2}):`, sampleItem[specialKey2] !== undefined,
              `Direct key:`, sampleItem[selectedSensor2] !== undefined);
          }
        }
      }
      
      // Create data for correlation chart - filter out entries with null values
      const corrData = filtered
        .filter(item => {
          const sensor1Value = selectedSensor1 === 'm1' ? item.m1 : getSensorValue(item, selectedSensor1);
          const sensor2Value = selectedSensor2 === 'm2' ? item.m2 : getSensorValue(item, selectedSensor2);
          return sensor1Value !== null && sensor1Value !== undefined && 
                 sensor2Value !== null && sensor2Value !== undefined;
        })
        .map(item => {
          const sensor1Value = selectedSensor1 === 'm1' ? item.m1 : getSensorValue(item, selectedSensor1);
          const sensor2Value = selectedSensor2 === 'm2' ? item.m2 : getSensorValue(item, selectedSensor2);
          
          let timestamp;
          try {
            timestamp = new Date(item.timestamp).getTime();
          } catch (e) {
            console.error("Error parsing timestamp for correlation:", item.timestamp);
            timestamp = 0; // Fallback
          }
          
          return {
            sensor1: sensor1Value,
            sensor2: sensor2Value,
            timestamp: timestamp
          };
        });
      
      setCorrelationData(corrData);
      
      // Create difference data - filter out entries with null values
      const differences = filtered
        .filter(item => {
          const sensor1Value = selectedSensor1 === 'm1' ? item.m1 : getSensorValue(item, selectedSensor1);
          const sensor2Value = selectedSensor2 === 'm2' ? item.m2 : getSensorValue(item, selectedSensor2);
          return sensor1Value !== null && sensor1Value !== undefined && 
                 sensor2Value !== null && sensor2Value !== undefined;
        })
        .map(item => {
          const sensor1Value = selectedSensor1 === 'm1' ? item.m1 : getSensorValue(item, selectedSensor1);
          const sensor2Value = selectedSensor2 === 'm2' ? item.m2 : getSensorValue(item, selectedSensor2);
          
          // Create the timestamp value as a pure number for recharts to handle correctly
          let timestamp;
          try {
            timestamp = new Date(item.timestamp).getTime();
          } catch (e) {
            console.error("Error parsing timestamp:", item.timestamp);
            timestamp = 0; // Fallback
          }
          
          // Create the formatted date string separately
          let formattedDate;
          try {
            formattedDate = format(new Date(item.timestamp), 'MM/dd/yyyy');
          } catch (e) {
            formattedDate = 'unknown date';
          }
          
          return {
            timestamp: timestamp,
            diff: sensor1Value - sensor2Value,
            formattedDate: formattedDate
          };
        });
      
      setDiffData(differences);
      
      // Debug difference data
      console.log(`Difference data points for ${selectedSensor1} vs ${selectedSensor2}:`, differences.length);
      if (differences.length === 0) {
        console.warn("No difference data available - check if both sensors have valid readings");
      } else {
        console.log("Sample diff data point:", differences[0]);
      }
      
      // Calculate statistics
      if (corrData.length > 0) {  // Use corrData which already has null values filtered out
        // Calculate correlation coefficient
        const n = corrData.length;
        const sensor1Values = corrData.map(item => item.sensor1);
        const sensor2Values = corrData.map(item => item.sensor2);
        
        const sensor1Avg = sensor1Values.reduce((a, b) => a + b, 0) / n;
        const sensor2Avg = sensor2Values.reduce((a, b) => a + b, 0) / n;
        
        let numerator = 0;
        let denominator1 = 0;
        let denominator2 = 0;
        
        for (let i = 0; i < n; i++) {
          numerator += (sensor1Values[i] - sensor1Avg) * (sensor2Values[i] - sensor2Avg);
          denominator1 += Math.pow(sensor1Values[i] - sensor1Avg, 2);
          denominator2 += Math.pow(sensor2Values[i] - sensor2Avg, 2);
        }
        
        const correlation = numerator / Math.sqrt(denominator1 * denominator2);
        
        // Calculate average absolute difference
        const diffs = differences.map(item => Math.abs(item.diff));
        const avgDiff = diffs.reduce((a, b) => a + b, 0) / diffs.length;
        
        // Calculate max difference
        const maxDiff = Math.max(...diffs);
        
        setStats({
          correlation: correlation.toFixed(2),
          averageDiff: avgDiff.toFixed(1),
          maxDiff: maxDiff.toFixed(1),
          sensor1Avg: sensor1Avg.toFixed(1),
          sensor2Avg: sensor2Avg.toFixed(1)
        });
      }
    }
  }, [data, timeRange, selectedSensor1, selectedSensor2, readingType]);
  
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">Error loading sensor data</Typography>
        <Typography>{error.message}</Typography>
      </Box>
    );
  }
  
  const renderDifferenceTooltip = (props) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }
    
    const { diff, formattedDate } = props.payload[0].payload;
    
    // Generate sensor labels based on selection
    const sensor1Label = selectedSensor1 === 'm1' ? 'Overall (m1)' : selectedSensor1;
    const sensor2Label = selectedSensor2 === 'm2' ? 'Overall (m2)' : selectedSensor2;
    
    return (
      <Box className="sensor-tooltip">
        <Typography variant="subtitle2">{formattedDate}</Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body2">
          <strong>Difference ({sensor1Label} - {sensor2Label}):</strong> {diff.toFixed(1)}
        </Typography>
      </Box>
    );
  };
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Sensor Comparison {selectedTunnel ? `(${selectedTunnel})` : ''}
        </Typography>
        
        {data?.tunnels && (
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="comparison-tunnel-select-label">Tunnel</InputLabel>
            <Select
              labelId="comparison-tunnel-select-label"
              id="comparison-tunnel-select"
              value={selectedTunnel || ''}
              label="Tunnel"
              onChange={(e) => {
                const newTunnelId = e.target.value || null;
                changeTunnel(newTunnelId);
                // Update URL to reflect the selected tunnel
                if (newTunnelId) {
                  navigate(`/comparison/${newTunnelId}`);
                } else {
                  navigate('/comparison');
                }
              }}
            >
              <MenuItem value="">All Tunnels</MenuItem>
              {data.tunnels.map(tunnel => (
                <MenuItem key={tunnel} value={tunnel}>{tunnel}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Correlation Coefficient
              </Typography>
              <Typography variant="h4" component="div">
                {stats.correlation}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                1.0 = perfect correlation
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Average Difference
              </Typography>
              <Typography variant="h4" component="div">
                {stats.averageDiff}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Absolute difference between sensors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Maximum Difference
              </Typography>
              <Typography variant="h4" component="div">
                {stats.maxDiff}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Largest divergence between sensors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Controls and Options */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Time Range */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="time-range-label">Time Range</InputLabel>
              <Select
                labelId="time-range-label"
                id="time-range-select"
                value={timeRange}
                label="Time Range"
                onChange={handleTimeRangeChange}
              >
                {timeRanges.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Reading Type */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="reading-type-label">Reading Type</InputLabel>
              <Select
                labelId="reading-type-label"
                id="reading-type-select"
                value={readingType}
                label="Reading Type"
                onChange={(e) => setReadingType(e.target.value)}
              >
                <MenuItem value="m1">M1 (Light)</MenuItem>
                <MenuItem value="m2">M2 (Visual)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Sensor 1 Selection */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={!selectedTunnel || availableSensors.length === 0}>
              <InputLabel id="sensor1-label">Sensor 1</InputLabel>
              <Select
                labelId="sensor1-label"
                id="sensor1-select"
                value={selectedSensor1}
                label="Sensor 1"
                onChange={(e) => setSelectedSensor1(e.target.value)}
              >
                <MenuItem value="m1">Overall Average (m1)</MenuItem>
                {availableSensors.map(sensor => (
                  <MenuItem key={sensor.id} value={sensor.id}>
                    {sensor.id} ({sensor.position} - {sensor.placement})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Sensor 2 Selection */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" disabled={!selectedTunnel || availableSensors.length === 0}>
              <InputLabel id="sensor2-label">Sensor 2</InputLabel>
              <Select
                labelId="sensor2-label"
                id="sensor2-select"
                value={selectedSensor2}
                label="Sensor 2"
                onChange={(e) => setSelectedSensor2(e.target.value)}
              >
                <MenuItem value="m2">Overall Average (m2)</MenuItem>
                {availableSensors.map(sensor => (
                  <MenuItem key={sensor.id} value={sensor.id}>
                    {sensor.id} ({sensor.position} - {sensor.placement})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Statistics */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box 
              sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: theme.palette.sensor1.main,
                mr: 1 
              }} 
            />
            <Typography variant="body2">
              {selectedSensor1 === 'm1' ? 'Overall' : selectedSensor1} Avg: {stats.sensor1Avg}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box 
              sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: theme.palette.sensor2.main,
                mr: 1 
              }} 
            />
            <Typography variant="body2">
              {selectedSensor2 === 'm2' ? 'Overall' : selectedSensor2} Avg: {stats.sensor2Avg}
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      {/* Combined Chart */}
      <Paper sx={{ p: 2, height: 400, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Sensor Readings Comparison ({readingType.toUpperCase()})
        </Typography>
        <Box sx={{ height: '90%' }}>
          {filteredData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                key={`chart-${selectedSensor1}-${selectedSensor2}-${readingType}`}  
                data={filteredData}
                margin={{
                  top: 15,
                  right: 30,
                  left: 20,
                  bottom: 15,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={(timestamp) => {
                    try {
                      return format(new Date(timestamp), 'MM/dd');
                    } catch (e) {
                      return '';
                    }
                  }}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(timestamp) => {
                    try {
                      return format(new Date(timestamp), 'MM/dd/yyyy');
                    } catch (e) {
                      return timestamp;
                    }
                  }}
                />
                <Legend />
                
                {/* First sensor */}
                <Line
                  key={`sensor1-${selectedSensor1}-${readingType}`}
                  type="monotone"
                  dataKey={selectedSensor1 === 'm1' ? readingType : `${selectedSensor1}_${readingType}`}
                  name={selectedSensor1 === 'm1' ? `Overall (${readingType})` : selectedSensor1}
                  stroke={theme.palette.sensor1.main}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  connectNulls
                  isAnimationActive={true}
                />
                
                {/* Second sensor */}
                <Line
                  key={`sensor2-${selectedSensor2}-${readingType}`}
                  type="monotone"
                  dataKey={selectedSensor2 === 'm2' ? readingType : `${selectedSensor2}_${readingType}`}
                  name={selectedSensor2 === 'm2' ? `Overall (${readingType})` : selectedSensor2}
                  stroke={theme.palette.sensor2.main}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6 }}
                  connectNulls
                  isAnimationActive={true}
                />
                
                {/* Threshold line if provided */}
                {data.threshold && (
                  <ReferenceLine
                    y={data.threshold}
                    stroke="#f44336"
                    strokeDasharray="3 3"
                    label={{ 
                      value: `Threshold: ${data.threshold}`, 
                      position: 'right',
                      fill: '#f44336'
                    }}
                  />
                )}
                
                {/* Wash event markers (vertical lines) */}
                {data.washDates && data.washDates.map((washDate, index) => (
                  <ReferenceLine
                    key={`wash-${index}`}
                    x={washDate}
                    stroke="#4caf50"
                    strokeDasharray="3 3"
                    label={{ 
                      value: 'Wash', 
                      position: 'top',
                      fill: '#4caf50',
                      fontSize: 10
                    }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography>No data available</Typography>
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Two Column Charts */}
      <Grid container spacing={3}>
        {/* Difference Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" gutterBottom>
              Sensor Difference ({selectedSensor1 === 'm1' ? 'Overall (m1)' : selectedSensor1} - {selectedSensor2 === 'm2' ? 'Overall (m2)' : selectedSensor2})
            </Typography>
            <Box sx={{ height: '90%' }}>
              {diffData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={diffData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(timestamp) => {
                        try {
                          return format(new Date(timestamp), 'MM/dd');
                        } catch (e) {
                          console.error("Error formatting date:", timestamp, e);
                          return '';
                        }
                      }}
                      type="number"
                      domain={['auto', 'auto']}
                      scale="time"
                    />
                    <YAxis 
                      label={{ value: 'Difference', angle: -90, position: 'insideLeft' }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={renderDifferenceTooltip} />
                    <Legend />
                    <ReferenceLine y={0} stroke="#000" strokeDasharray="3 3" />
                    <Line
                      type="monotone"
                      dataKey="diff"
                      name={`Difference (${selectedSensor1} - ${selectedSensor2})`}
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 6 }}
                      connectNulls={true}
                      isAnimationActive={true}
                      animationDuration={1000}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography>No data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        
        {/* Correlation Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" gutterBottom>
              Sensor Correlation
            </Typography>
            <Box sx={{ height: '90%' }}>
              {correlationData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart
                    margin={{
                      top: 20,
                      right: 20,
                      bottom: 20,
                      left: 20,
                    }}
                  >
                    <CartesianGrid />
                    <XAxis 
                      type="number" 
                      dataKey="sensor1" 
                      name={selectedSensor1 === 'm1' ? 'Overall (m1)' : selectedSensor1}
                      label={{ 
                        value: selectedSensor1 === 'm1' ? 'Overall (m1)' : selectedSensor1, 
                        position: 'insideBottom', 
                        offset: -5 
                      }} 
                    />
                    <YAxis 
                      type="number" 
                      dataKey="sensor2" 
                      name={selectedSensor2 === 'm2' ? 'Overall (m2)' : selectedSensor2}
                      label={{ 
                        value: selectedSensor2 === 'm2' ? 'Overall (m2)' : selectedSensor2, 
                        angle: -90, 
                        position: 'insideLeft' 
                      }} 
                    />
                    <ZAxis range={[20, 20]} />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter 
                      name="Sensors" 
                      data={correlationData} 
                      fill={theme.palette.primary.main} 
                    />
                    {/* Perfect correlation reference line */}
                    <ReferenceLine 
                      segment={[
                        { x: 0, y: 0 }, 
                        { 
                          x: Math.max(...correlationData.map(d => d.sensor1)), 
                          y: Math.max(...correlationData.map(d => d.sensor1)) 
                        }
                      ]} 
                      stroke="red" 
                      strokeDasharray="3 3" 
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography>No data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComparisonView;